import {
  Box,
  Button,
  Flex,
  FlexProps,
  Heading,
  Text,
} from '@everlywell/ui-kit';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import ShapeBackground from 'components/ShapeBackground';
import { Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { ImageType } from 'utils/types';
import { CallToAction } from 'utils/types/contentfulTypes';

export type TakeoverPromoSectionProps = {
  heading: string;
  description: string; // richtext
  primaryCard: {
    backgroundColor?: string | null; // default to 'utility.sale'
    mobileImage: ImageType | null;
    desktopImage: ImageType | null;
    heading: string;
    description: string;
    cta: CallToAction;
  };

  secondaryCard?: {
    mobileImage: ImageType | null;
    desktopImage: ImageType | null;
    cta: CallToAction;
  };
};

/**
 * Renders a takeover promo section with a primary and secondary card.
 * The primary card is the main card with a background image and a CTA button.
 * The secondary card is a smaller card with a product image and a CTA button.
 */
const TakeoverPromoSection = (props: TakeoverPromoSectionProps) => {
  const { heading, description, primaryCard, secondaryCard } = props;

  return (
    <SectionContainer size={['sm', null, 'xl']}>
      <Flex
        flexDirection="column"
        alignItems="center"
        gap={3}
        marginBottom={[16, null, 20]}
      >
        {heading ? (
          <Heading size="3xl" color="tints.black" textAlign="center">
            {heading}
          </Heading>
        ) : null}

        {description ? (
          <Text
            size="xl"
            color="tints.darkGrey"
            textAlign="center"
            maxWidth={600}
          >
            {description}
          </Text>
        ) : null}
      </Flex>
      <Flex flexDirection={['column', null, 'row']} gap={[6, null, 4]}>
        <ShapeBackground
          desktopImage={primaryCard.desktopImage}
          mobileImage={primaryCard.mobileImage}
          backgroundColor={primaryCard.backgroundColor ?? 'utility.sale'}
          maxHeight={['none', null, 'unset']}
          height={['auto', null, 'unset']}
          flex={1}
        >
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Heading
              size="2xl"
              marginBottom="3"
              textAlign="center"
              color="tints.white"
            >
              {primaryCard.heading}
            </Heading>
            <Heading
              as="p"
              size="md"
              marginBottom="8"
              textAlign="center"
              color="tints.white"
            >
              {primaryCard.description}
            </Heading>
            <Button
              as={Link}
              to={primaryCard.cta.url}
              variant="primary"
              colorScheme="whiteCS"
              id={`${primaryCard.cta.label}-promo`}
              width={['100%', null, 'fit-content']}
              size="md"
              onClick={() => {
                analytics.track({
                  event: ANALYTICS.EVENTS.CLICKED_BUTTON,
                  data: {
                    sectionHeader: primaryCard.heading,
                    contentfulSection: 'TakeoverPromo',
                    ctaText: primaryCard.cta.label,
                  },
                });
              }}
            >
              {primaryCard.cta.label}
            </Button>
          </Flex>
        </ShapeBackground>

        {secondaryCard ? (
          <ShopAllProductsCard
            width={['100%', null, '33.333%']}
            transform={['none', null, 'translateY(40px)']}
            image={secondaryCard.mobileImage}
            cta={{
              label: secondaryCard.cta.label ?? '',
              url: secondaryCard.cta.url ?? '',
            }}
          />
        ) : null}
      </Flex>
    </SectionContainer>
  );
};

export default TakeoverPromoSection;

type ShopAllProductsCardProps = FlexProps & {
  image: ImageType | null;
  cta: CallToAction;
};

const ShopAllProductsCard = (props: ShopAllProductsCardProps) => {
  const { image, cta, ...flexProps } = props;

  return (
    <Flex
      flexDirection="column"
      justifyContent="space-between"
      alignItems="center"
      padding={8}
      paddingTop={0}
      marginTop={4}
      backgroundColor="tints.lightCream"
      borderRadius={12}
      {...flexProps}
    >
      <Box marginTop={[-4]}>
        {image ? (
          <GatsbyImage
            image={image.gatsbyImageData}
            alt={image.title ?? ''}
            objectFit="contain"
            style={{
              maxWidth: '100%',
              height: 'auto',
            }}
          />
        ) : null}
      </Box>

      <Button
        as={Link}
        to={cta.url}
        variant="primary"
        colorScheme="whiteCS"
        size="md"
        width={['100%', null, 'fit-content']}
        onClick={() => {
          analytics.track({
            event: ANALYTICS.EVENTS.CLICKED_BUTTON,
            data: {
              sectionHeader: cta.label,
              contentfulSection: 'TakeoverPromo',
              ctaText: cta.label,
            },
          });
        }}
      >
        {cta.label}
      </Button>
    </Flex>
  );
};
